<template>
    <b-modal :visible="true" :title="modalTitle" size="lg" hide-footer @hidden="$emit('close')">
        <!-- Existing thread: show messages -->
        <div v-if="thread" class="thread-history mb-3">
            <div v-if="loading" class="text-center"><b-spinner small /></div>
            <div v-else>
                <div v-for="m in messages" :key="m.id"
                    :class="['message-row mb-2 p-2', m.inbound ? 'bg-light' : 'bg-white border']">
                    <div class="d-flex justify-content-between">
                        <strong>
                            <i :class="m.inbound ? 'fa fa-user-md text-success' : 'fa fa-user-nurse text-primary'"
                                class="mr-1"></i>
                            {{ m.authorUserName }}
                        </strong>
                        <small class="text-muted">{{ formatDate(m.createdDate) }}</small>
                    </div>
                    <div class="mt-1" style="white-space: pre-wrap;">{{ m.body }}</div>
                </div>
                <div v-if="messages.length === 0" class="text-muted">No messages yet.</div>
            </div>
        </div>

        <!-- Initiation only: prescriber + practice picker -->
        <div v-if="!thread && initiateContext">
            <b-form-group label="Prescriber" label-cols-md="3">
                <div v-if="prescriberLockedFromContext" class="form-control-plaintext py-0">
                    <strong>{{ prescriberDisplayName }}</strong>
                    <small class="text-muted ml-2">NPI {{ form.prescriberNPI }}</small>
                </div>
                <SearchComponent v-else v-model="selectedPrescriber"
                    :id="selectedPrescriber && +selectedPrescriber.id"
                    label="Prescriber" :hide-label="true" :disabled="!canSend"
                    search-u-r-l="/Prescriber"
                    :static-options="patientPrescribers"
                    :results-modifier="prescriberResultsModifier"
                    @input="onPrescriberSelected" />
            </b-form-group>
            <b-form-group v-if="practicesLoaded && practiceOptions.length > 1" label="Practice" label-cols-md="3">
                <b-form-select v-model="form.practiceId" :options="practiceOptions" :disabled="!canSend">
                    <template #first>
                        <b-form-select-option :value="null" disabled>-- Choose a practice --</b-form-select-option>
                    </template>
                </b-form-select>
            </b-form-group>
        </div>

        <!-- Reply / new message body -->
        <b-form-group label="Message" label-cols-md="3">
            <b-form-textarea v-model="form.body" rows="4" max-rows="8" maxlength="2000" :disabled="!canSend"
                placeholder="Plain text only. 2000 characters max." />
            <small class="text-muted d-block text-right">{{ form.body.length }} / 2000</small>
        </b-form-group>

        <div class="d-flex justify-content-between align-items-center">
            <div>
                <b-button v-if="thread && canSend && !thread.isResolved" variant="success" size="sm"
                    @click="resolveThread(true)" :disabled="saving">
                    Mark Resolved
                </b-button>
                <b-button v-if="thread && canSend && thread.isResolved" variant="secondary" size="sm"
                    @click="resolveThread(false)" :disabled="saving">
                    Reopen
                </b-button>
            </div>
            <b-alert v-if="!thread && initiateContext && practicesLoaded && practiceOptions.length === 0"
                show variant="danger" class="mb-0 py-2 ml-3 flex-fill">
                No practice could be resolved for this prescriber. The prescriber must be linked to a practice in
                the provider portal before a communication can be sent.
            </b-alert>
            <b-button v-else variant="primary" @click="send" :disabled="saving || !canSendMessage">
                <b-spinner v-if="saving" small class="mr-1" />
                Send
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Axios from "axios";
import SearchComponent from "@/components/SearchComponent.vue";
import { Prescriber } from "@/models/Prescriber";
import {
    providerCommunicationsService,
    providerCommunicationsPermissions,
    ProviderCommunicationThread,
    ProviderCommunicationMessage,
    PracticeOption,
} from "@/services/providerCommunicationsService";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import moment from "moment";

interface InitiateContext {
    patientId: number;
    prescriberNPI?: string;
    prescriberFirstName?: string | null;
    prescriberLastName?: string | null;
    practiceId?: number | null;
    storeID?: number | null;
    rxNumber?: number | null;
    rfNumber?: number | null;
}

@Component({ components: { SearchComponent } })
export default class ProviderCommunicationModal extends Vue {
    @Prop({ type: Object, default: null }) thread!: ProviderCommunicationThread | null;
    @Prop({ type: Object, default: null }) initiateContext!: InitiateContext | null;
    @Prop({ type: Number, required: true }) patientId!: number;

    messages: ProviderCommunicationMessage[] = [];
    loading = false;
    saving = false;
    practiceOptions: { value: number; text: string }[] = [];
    practicesLoaded = false;
    selectedPrescriber: Prescriber = new Prescriber();
    patientPrescribers: Prescriber[] = [];

    form = {
        prescriberNPI: "",
        prescriberFirstName: "" as string | null,
        prescriberLastName: "" as string | null,
        practiceId: null as number | null,
        body: "",
    };

    get prescriberLockedFromContext(): boolean {
        return !!this.initiateContext?.prescriberNPI;
    }

    get prescriberDisplayName(): string {
        const parts = [this.form.prescriberLastName, this.form.prescriberFirstName].filter(Boolean);
        return parts.length ? parts.join(", ") : "(name unavailable)";
    }

    get canSend(): boolean {
        return (this as any).$permissions.some(
            (p: { permissionCodeName: string }) => p.permissionCodeName === providerCommunicationsPermissions.send
        );
    }

    get canSendMessage(): boolean {
        if (!this.canSend) return false;
        if (!this.form.body.trim()) return false;
        if (this.thread) return true;
        // Initiate flow: must have NPI + practice resolved
        return !!this.form.prescriberNPI && !!this.form.practiceId;
    }

    get modalTitle(): string {
        if (this.thread) {
            const parts = [this.thread.prescriberFirstName, this.thread.prescriberLastName].filter(Boolean);
            return parts.length
                ? `Provider Communication: ${parts.join(" ")}`
                : `Provider Communication: NPI ${this.thread.prescriberNPI}`;
        }
        return "New Provider Communication";
    }

    async created() {
        if (this.thread) {
            await this.loadThread();
        } else if (this.initiateContext) {
            this.form.prescriberNPI = this.initiateContext.prescriberNPI ?? "";
            this.form.prescriberFirstName = this.initiateContext.prescriberFirstName ?? null;
            this.form.prescriberLastName = this.initiateContext.prescriberLastName ?? null;
            if (this.form.prescriberNPI) await this.resolvePractices();
            if (!this.prescriberLockedFromContext) await this.fetchPatientPrescribers();
        }
    }

    async fetchPatientPrescribers() {
        if (!this.patientId) {
            this.patientPrescribers = [];
            return;
        }
        try {
            const response = await Axios.get(`/api/Prescriber/by-patient/${this.patientId}`);
            this.patientPrescribers = (response.data || []).map((p: Prescriber) => new Prescriber(p));
        } catch (err) {
            this.patientPrescribers = [];
            (this as any).$notification(NotificationOptions.error(err));
        }
    }

    prescriberResultsModifier(searchResults: any[], params?: { searchTerm?: string }) {
        const searchTerm = (params?.searchTerm ?? "").trim();
        // While the user is searching, show only API matches — recent prescribers
        // would dilute the results and led to incorrect selections (SD-3952).
        if (searchTerm) return searchResults;

        if (!this.patientPrescribers.length) return searchResults;

        return [
            { heading: true, label: "Patient's Prescribers" },
            ...this.patientPrescribers,
        ];
    }

    async onPrescriberSelected(prescriber: Prescriber | null) {
        this.form.practiceId = null;
        this.practiceOptions = [];
        this.practicesLoaded = false;
        if (!prescriber || !prescriber.id) {
            this.selectedPrescriber = new Prescriber();
            this.form.prescriberNPI = "";
            this.form.prescriberFirstName = null;
            this.form.prescriberLastName = null;
            return;
        }
        this.selectedPrescriber = new Prescriber(prescriber);
        this.form.prescriberNPI = prescriber.npiNumber ?? "";
        this.form.prescriberFirstName = prescriber.firstName ?? null;
        this.form.prescriberLastName = prescriber.lastName ?? null;
        if (this.form.prescriberNPI) {
            await this.resolvePractices();
        } else {
            // Prescriber has no NPI on file — practice lookup needs an NPI, so
            // surface this as the "no practice resolvable" state rather than
            // silently leaving the alert hidden.
            this.practicesLoaded = true;
        }
    }

    async loadThread() {
        if (!this.thread) return;
        this.loading = true;
        try {
            const detail = await providerCommunicationsService.getThread(this.thread.id);
            this.messages = detail.messages;
            await providerCommunicationsService.markRead(this.thread.id);
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    async resolvePractices() {
        if (!this.form.prescriberNPI) return;
        this.practicesLoaded = false;
        try {
            const practices = await providerCommunicationsService.getPracticesForNpi(this.form.prescriberNPI);
            this.practiceOptions = practices.map((p: PracticeOption) => ({
                value: p.id,
                text: p.name ?? `Practice ${p.id}`,
            }));
            if (this.practiceOptions.length === 1) {
                this.form.practiceId = this.practiceOptions[0].value;
            }
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.practicesLoaded = true;
        }
    }

    async send() {
        if (!this.canSendMessage) return;
        this.saving = true;
        try {
            if (this.thread) {
                await providerCommunicationsService.reply(this.thread.id, {
                    body: this.form.body,
                    storeID: this.initiateContext?.storeID ?? null,
                    rxNumber: this.initiateContext?.rxNumber ?? null,
                    rfNumber: this.initiateContext?.rfNumber ?? null,
                });
            } else {
                await providerCommunicationsService.initiate({
                    patientId: this.patientId,
                    prescriberNPI: this.form.prescriberNPI,
                    prescriberFirstName: this.form.prescriberFirstName,
                    prescriberLastName: this.form.prescriberLastName,
                    practiceId: this.form.practiceId,
                    storeID: this.initiateContext?.storeID ?? null,
                    rxNumber: this.initiateContext?.rxNumber ?? null,
                    rfNumber: this.initiateContext?.rfNumber ?? null,
                    body: this.form.body,
                });
            }
            (this as any).$notification(NotificationOptions.success("Message sent."));
            this.$emit("saved");
            this.$emit("close");
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.saving = false;
        }
    }

    async resolveThread(isResolved: boolean) {
        if (!this.thread) return;
        this.saving = true;
        try {
            await providerCommunicationsService.setResolved(this.thread.id, isResolved);
            (this as any).$notification(
                NotificationOptions.success(isResolved ? "Thread resolved." : "Thread reopened.")
            );
            this.$emit("saved");
            this.$emit("close");
        } catch (err) {
            (this as any).$notification(NotificationOptions.error(err));
        } finally {
            this.saving = false;
        }
    }

    formatDate(value: string): string {
        return moment.utc(value).local().format("M/D/YYYY h:mm a");
    }
}
</script>

<style scoped>
.thread-history {
    max-height: 40vh;
    overflow-y: auto;
}

.message-row {
    border-radius: 4px;
}
</style>
